import jsPDF from 'jspdf';

/**
 * Build an 80mm thermal receipt PDF from stored/regenerable payload.
 * @returns {jsPDF}
 */
export function buildThermalReceiptDoc(receiptPayload) {
  const {
    companySnapshot: co = {},
    customerInfo = {},
    vehicleInfo = {},
    receiptDetails = {},
    services: serviceLines = [],
  } = receiptPayload || {};

  const pageWidth = 80;
  const margin = 4;
  const contentWidth = pageWidth - margin * 2;
  const estimatedHeight = Math.max(140, 85 + serviceLines.length * 10 + 55);

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [pageWidth, estimatedHeight],
  });

  const centerX = pageWidth / 2;
  let y = 6;

  const drawDivider = () => {
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.line(margin, y, pageWidth - margin, y);
    y += 3;
  };

  const wrapCenter = (text, fontSize) => {
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(String(text || ''), contentWidth);
    lines.forEach((line) => {
      doc.text(line, centerX, y, { align: 'center' });
      y += fontSize * 0.45;
    });
  };

  doc.setFont('helvetica', 'bold');
  wrapCenter(co.shortName || co.displayName || 'Car Wash', 11);

  doc.setFont('helvetica', 'normal');
  wrapCenter(co.addressFull || '', 7);
  wrapCenter(`TRN: ${co.trn || ''}`, 7);
  wrapCenter(`Tel: ${co.tel || ''}`, 7);
  y += 1;
  drawDivider();

  doc.setFont('helvetica', 'bold');
  wrapCenter('TAX INVOICE', 10);
  // Large vehicle number in place of "(Thermal Receipt)" only
  doc.setFontSize(16);
  const vehicleNoText = String(vehicleInfo.vehicleNo || '-');
  const vehicleNoLines = doc.splitTextToSize(vehicleNoText, contentWidth);
  vehicleNoLines.forEach((line) => {
    doc.text(line, centerX, y, { align: 'center' });
    y += 6;
  });
  y += 1;
  drawDivider();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text(`Receipt No: ${receiptDetails.receiptNumber || '-'}`, margin, y);
  y += 4;
  doc.text(`Date: ${receiptDetails.receiptDate || '-'}`, margin, y);
  y += 4;
  doc.text(`Customer: ${customerInfo.name || '-'}`, margin, y);
  y += 4;
  if (customerInfo.contactNo) {
    doc.text(`Contact: ${customerInfo.contactNo}`, margin, y);
    y += 4;
  }
  doc.text(
    `Vehicle: ${vehicleInfo.vehicleType || '-'} / ${vehicleInfo.vehicleNo || '-'}`,
    margin,
    y
  );
  y += 4;
  if (receiptDetails.paymentMethod) {
    doc.text(`Payment: ${receiptDetails.paymentMethod}`, margin, y);
    y += 4;
  }
  drawDivider();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text('Item', margin, y);
  doc.text('AED', pageWidth - margin, y, { align: 'right' });
  y += 3;
  drawDivider();

  doc.setFont('helvetica', 'normal');
  serviceLines.forEach((line, index) => {
    const nameLines = doc.splitTextToSize(
      `${index + 1}. ${line.name || 'Service'}`,
      contentWidth - 18
    );
    nameLines.forEach((nl, idx) => {
      doc.text(nl, margin, y);
      if (idx === 0) {
        doc.text(
          parseFloat(line.priceWithoutVAT || 0).toFixed(2),
          pageWidth - margin,
          y,
          { align: 'right' }
        );
      }
      y += 3.5;
    });
    y += 1;
  });

  drawDivider();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('Subtotal:', margin, y);
  doc.text(
    parseFloat(receiptDetails.subtotal || 0).toFixed(2),
    pageWidth - margin,
    y,
    { align: 'right' }
  );
  y += 4;
  doc.text('VAT (5%):', margin, y);
  doc.text(
    parseFloat(receiptDetails.vatAmount || 0).toFixed(2),
    pageWidth - margin,
    y,
    { align: 'right' }
  );
  y += 4;
  doc.setFontSize(9);
  doc.text('TOTAL:', margin, y);
  doc.text(
    parseFloat(receiptDetails.totalAmount || 0).toFixed(2),
    pageWidth - margin,
    y,
    { align: 'right' }
  );
  y += 5;
  drawDivider();

  doc.setFont('helvetica', 'normal');
  wrapCenter('Thank you for your business!', 7);
  wrapCenter('Prices include VAT where applicable', 6.5);

  return doc;
}

/** Best-effort print dialog; never throws to callers.
 * Prefers a hidden iframe so it still works after async API calls
 * (window.open is often blocked once the user-gesture is gone).
 */
export function openThermalPrintDialog(doc) {
  try {
    const blobUrl = doc.output('bloburl');

    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'thermal-receipt-print');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => {
        try {
          if (iframe.parentNode) document.body.removeChild(iframe);
        } catch (_) {
          /* ignore */
        }
        try {
          URL.revokeObjectURL(blobUrl);
        } catch (_) {
          /* ignore */
        }
      }, 60000);
    };

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (iframeErr) {
        console.warn('Thermal iframe print failed, trying window.open:', iframeErr);
        try {
          window.open(blobUrl, '_blank');
        } catch (openErr) {
          console.warn('Thermal window.open also failed:', openErr);
        }
      } finally {
        cleanup();
      }
    };
  } catch (printErr) {
    console.warn('Thermal print dialog could not be opened:', printErr);
  }
}

export function downloadThermalReceiptPdf(receiptPayload, fileName) {
  const doc = buildThermalReceiptDoc(receiptPayload);
  const safeFile =
    fileName ||
    `ThermalReceipt_${receiptPayload?.receiptDetails?.receiptNumber || Date.now()}.pdf`;
  doc.save(safeFile);
  return doc;
}
